/**
 * Date parsing utilities — kept tiny on purpose.
 *
 * The bug we keep hitting: ``new Date("2026-05-07")`` parses the string as
 * ISO UTC midnight. In a negative-offset timezone like ET (UTC-4) that's
 * 8 PM the previous evening, so anything that displays a weekday or day-of-month
 * silently slides back a day.
 *
 * ``parseLocalDate`` instead constructs a ``Date`` from local Y/M/D so the
 * weekday and calendar day always match what the user typed in the admin UI.
 */

/**
 * Parse a YYYY-MM-DD string as a LOCAL calendar date.
 * Falls back to ``new Date(input)`` when the input has a time component
 * (those parse correctly already).
 *
 * @param {string|Date|null|undefined} input
 * @returns {Date|null}
 */
export const parseLocalDate = (input) => {
  if (!input) return null;
  if (input instanceof Date) return input;
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(input));
  if (m) {
    return new Date(parseInt(m[1], 10), parseInt(m[2], 10) - 1, parseInt(m[3], 10));
  }
  // ISO datetime / RFC3339 → JS Date already does this correctly.
  return new Date(input);
};

/**
 * Convenience: format a Y-M-D (or any date input) using ``toLocaleDateString``
 * but always rendered against the user's local calendar.
 */
export const formatLocalDate = (input, options = { month: 'short', day: 'numeric' }) => {
  const d = parseLocalDate(input);
  if (!d || isNaN(d.getTime())) return '';
  return d.toLocaleDateString('en-US', options);
};
